import React, { useState, useEffect, useMemo, useRef } from 'react';
import PTCardTV from './PTCardTV';
import { ChevronLeft, ChevronRight, Play, Pause, Monitor, Grid } from 'lucide-react';
import { PT } from './types';

interface TVCarouselProps {
  pts: PT[];
  onExitTVMode: () => void;
  onViewDetails?: (pt: PT) => void;
}

const TVCarousel: React.FC<TVCarouselProps> = ({ pts, onExitTVMode, onViewDetails }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const wakeLockRef = useRef<any>(null);

  const SLIDE_INTERVAL = 5000; // 5 seconds
  const PTS_PER_SLIDE = 4;

  // Calculate deadline info - all PTs have same deadline: 30 Juni
  const deadlineInfo = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();

    // Deadline 30 Juni - pilih tahun ini atau tahun depan (mana yang belum lewat)
    let deadline = new Date(currentYear, 5, 30); // Juni = 5 (0-indexed)
    if (deadline < today) {
      // Jika 30 Juni tahun ini sudah lewat, pakai tahun depan
      deadline = new Date(currentYear + 1, 5, 30);
    }

    const timeDiff = deadline.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    // Count incomplete PTs
    const incompletePTs = pts.filter(pt => pt.progress_percentage < 100).length;
    const completedPTs = pts.filter(pt => pt.progress_percentage >= 100).length;

    return {
      deadline,
      daysRemaining,
      incompletePTs,
      completedPTs,
      totalPTs: pts.length
    };
  }, [pts]);

  // Sort PTs by progress (worst first)
  const sortedPTs = [...pts].sort((a, b) => a.progress_percentage - b.progress_percentage);

  // Calculate total slides
  const totalSlides = Math.ceil(sortedPTs.length / PTS_PER_SLIDE);

  // Get PTs for current slide
  const getCurrentSlidePTs = () => {
    const start = currentSlide * PTS_PER_SLIDE;
    const end = start + PTS_PER_SLIDE;
    return sortedPTs.slice(start, end);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
      setCountdown(5);
    }, SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, totalSlides]);

  // Countdown timer
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return 5;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // === KEEP SCREEN AWAKE FOR TV MODE ===

  // 1. Wake Lock API - Prevent screen from sleeping (modern browsers)
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
          console.log('Wake Lock activated - screen will stay on');

          wakeLockRef.current?.addEventListener('release', () => {
            console.log('Wake Lock released');
          });
        }
      } catch (err) {
        console.log('Wake Lock not supported or failed:', err);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock if page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      wakeLockRef.current?.release();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 2. Keep-alive activity - Simulate minimal activity to prevent browser sleep
  useEffect(() => {
    // Trigger a tiny scroll every 30 seconds (invisible to user)
    const keepAliveInterval = setInterval(() => {
      // Create a tiny DOM update to keep browser active
      const dummy = document.createElement('div');
      dummy.style.position = 'absolute';
      dummy.style.left = '-9999px';
      document.body.appendChild(dummy);
      setTimeout(() => dummy.remove(), 10);

      // Also dispatch a synthetic event
      window.dispatchEvent(new Event('keepalive'));
    }, 30000); // Every 30 seconds

    return () => clearInterval(keepAliveInterval);
  }, []);

  // 3. Auto-reload page every 30 minutes (fallback to refresh data and prevent any timeout)
  useEffect(() => {
    const autoReloadInterval = setInterval(() => {
      console.log('Auto-reloading page to keep TV mode fresh...');
      window.location.reload();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(autoReloadInterval);
  }, []);

  // 4. Prevent right-click context menu (cleaner TV experience)
  useEffect(() => {
    const preventContext = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', preventContext);
    return () => document.removeEventListener('contextmenu', preventContext);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevSlide();
          break;
        case 'ArrowRight':
          goToNextSlide();
          break;
        case ' ':
          e.preventDefault();
          togglePause();
          break;
        case 'Escape':
          onExitTVMode();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setCountdown(5);
  };

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setCountdown(5);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setCountdown(5);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <div className="h-screen w-screen bg-gray-900 text-white flex flex-col overflow-hidden">
      {/* News Ticker / Marquee - Deadline Info - BIGGER TEXT */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-900 border-b border-blue-700 overflow-hidden flex-shrink-0">
        <div className="py-2 flex items-center text-base">
          <div className="bg-yellow-500 text-blue-900 px-4 py-1 text-base font-bold flex-shrink-0 z-10">
            INFO
          </div>
          <div className="overflow-hidden flex-1 relative">
            <div className="animate-marquee whitespace-nowrap inline-block">
              <span className="inline-flex items-center mx-6">
                <span className="text-yellow-400 font-semibold">PT Selesai:</span>
                <span className="bg-emerald-600 text-white px-3 py-1 rounded text-base font-bold ml-2">
                  {deadlineInfo.completedPTs} / {deadlineInfo.totalPTs}
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-white">Deadline:</span>
                <span className="text-blue-200 ml-2 font-semibold">
                  {deadlineInfo.deadline.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-white">Sisa Waktu:</span>
                <span className={`ml-2 px-3 py-1 rounded text-base font-bold ${
                  deadlineInfo.daysRemaining <= 30 ? 'bg-red-500 text-white' :
                  deadlineInfo.daysRemaining <= 60 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.daysRemaining} hari
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-orange-400">Belum Selesai:</span>
                <span className={`ml-2 px-3 py-1 rounded text-base font-bold ${
                  deadlineInfo.incompletePTs > 10 ? 'bg-red-500 text-white' :
                  deadlineInfo.incompletePTs > 5 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.incompletePTs} PT
                </span>
              </span>
              {/* Duplicate for seamless loop */}
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-yellow-400 font-semibold">PT Selesai:</span>
                <span className="bg-emerald-600 text-white px-3 py-1 rounded text-base font-bold ml-2">
                  {deadlineInfo.completedPTs} / {deadlineInfo.totalPTs}
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-white">Deadline:</span>
                <span className="text-blue-200 ml-2 font-semibold">
                  {deadlineInfo.deadline.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-white">Sisa Waktu:</span>
                <span className={`ml-2 px-3 py-1 rounded text-base font-bold ${
                  deadlineInfo.daysRemaining <= 30 ? 'bg-red-500 text-white' :
                  deadlineInfo.daysRemaining <= 60 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.daysRemaining} hari
                </span>
              </span>
              <span className="text-blue-400 mx-4">|</span>
              <span className="inline-flex items-center mx-6">
                <span className="text-orange-400">Belum Selesai:</span>
                <span className={`ml-2 px-3 py-1 rounded text-base font-bold ${
                  deadlineInfo.incompletePTs > 10 ? 'bg-red-500 text-white' :
                  deadlineInfo.incompletePTs > 5 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.incompletePTs} PT
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Header - BIGGER */}
      <div className="flex-shrink-0 px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Monitor size={28} className="text-blue-400" />
            <div>
              <h1 className="text-lg font-bold leading-tight">TV Display Mode</h1>
              <p className="text-gray-400 text-sm">
                Slide {currentSlide + 1}/{totalSlides} | {sortedPTs.length} PTs | {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Countdown */}
            <div className="bg-gray-700 px-3 py-1.5 rounded text-base">
              <span className="text-gray-400">Next:</span>
              <span className="ml-2 font-bold text-blue-400 text-lg">
                {isPaused ? '||' : `${countdown}s`}
              </span>
            </div>

            {/* Play/Pause */}
            <button
              onClick={togglePause}
              className="bg-gray-700 hover:bg-gray-600 p-2 rounded transition"
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? <Play size={20} /> : <Pause size={20} />}
            </button>

            {/* Exit TV Mode */}
            <button
              onClick={onExitTVMode}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition flex items-center gap-2 text-base font-medium"
            >
              <Grid size={18} />
              <span>Exit</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - CARDS */}
      <div className="flex-1 p-3 overflow-hidden">
        {/* PT Cards Grid (2x2) */}
        <div className="h-full grid grid-cols-2 grid-rows-2 gap-3">
          {getCurrentSlidePTs().map((pt) => (
            <div key={pt.id} className="min-h-0 overflow-hidden">
              <PTCardTV pt={pt} onViewDetails={onViewDetails} />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-shrink-0 py-2 bg-gray-800 border-t border-gray-700">
        <div className="flex justify-center items-center gap-3">
          {/* Previous Button */}
          <button
            onClick={goToPrevSlide}
            className="bg-gray-700 hover:bg-gray-600 p-2 rounded-full transition"
            disabled={totalSlides <= 1}
          >
            <ChevronLeft size={24} />
          </button>

          {/* Slide Indicators */}
          <div className="flex gap-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-3 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-blue-500 w-8'
                    : 'bg-gray-600 hover:bg-gray-500 w-3'
                }`}
                title={`Slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={goToNextSlide}
            className="bg-gray-700 hover:bg-gray-600 p-2 rounded-full transition"
            disabled={totalSlides <= 1}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      {/* Marquee Animation Style */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default TVCarousel;
